import { batch as reduxBatch, connect as reduxConnect } from "react-redux";
import { createSelector as defaultCreateSelector } from "@reduxjs/toolkit";
import AsyncState, {
	AsyncActionCreatorsWithThunk,
	AsyncActionHandler,
	asyncStateActionCreatorsThunk,
	DefaultMeta,
	Meta,
} from "react-async-stateful";
import { GetStoreState } from "../../store/RootReducer";

export interface Action<P = any, T = string> {
	type: T;
	payload: P;
}

export type Thunk = (dispatch: Dispatch, getState: GetStoreState) => void;

export type Dispatch<P = any, V = any> = (
	action: Action<P> | Thunk,
) => Promise<V>;

export type TypedReducer<S> = (state: S, action: Action) => S;

export const batch = reduxBatch;

export const createSelector = defaultCreateSelector;

type ActionHandler<T> = (
	action: Action<T>,
	dispatch: Dispatch,
	getState: GetStoreState,
) => any;

type ActionCreator<P> = ((payload: P) => Action<P> | Thunk) & {
	type: string;
};

export const asyncStateProxy = <T, R>(
	asyncState: AsyncState<T>,
	accessor: (value: T | undefined) => R,
	defaultValue: R | undefined = undefined,
): AsyncState<R> => {
	let value = asyncState.resolved ? accessor(asyncState.value) : defaultValue;
	if (value === undefined) {
		value = defaultValue;
	}

	return {
		...asyncState,
		defaultValue,
		value: value,
	} as any;
};

export const actionCreator = <P>(
	type: string,
	handler?: ActionHandler<P>,
): ActionCreator<P> => {
	const creator = (payload: P) => {
		const action: Action<P> = {
			type,
			payload,
		};

		if (handler) {
			return async (dispatch: Dispatch<P>, getState: GetStoreState) => {
				const result = handler(action, dispatch, getState) || action;
				await dispatch(result);
				return result;
			};
		}

		return action;
	};
	creator.type = type;
	creator.toString = () => type;
	return creator;
};

export const createReducerMap = <T>(
	map: Record<string, TypedReducer<T>>,
): TypedReducer<T> => {
	return (state, action) => {
		if (action.type in map) {
			return map[action.type](state, action);
		}
		return state;
	};
};

export const isInitAction = (action: Action) => {
	// need both because of redux dev tools
	return (
		action &&
		action.type &&
		(action.type === "@@INIT" || action.type.startsWith("@@redux/INIT"))
	);
};

/**
 * @deprecated
 */
export const old__connect = (
	mapStateToProps: any,
	mapDispatchToProps: any,
	mergeProps: any,
	options: any,
) => {
	return (target: any) =>
		reduxConnect(mapStateToProps, mapDispatchToProps, mergeProps, {
			...(options || {}),
			forwardRef: true,
		})(target);
};

export function asyncActionCreators<P, V, M extends Meta = DefaultMeta>(
	type: string,
	handler?: AsyncActionHandler<any, P, V, M>,
): AsyncActionCreatorsWithThunk<P, V, M> {
	return asyncStateActionCreatorsThunk(type, handler);
}

export const composeReducer = <T>(
	initialState: T,
	reducersOrInitializer: TypedReducer<T>[] | ((initialState: T) => T),
	reducers?: TypedReducer<T>[],
): TypedReducer<T> => {
	const initializer =
		typeof reducersOrInitializer === "function" ? reducersOrInitializer : null;

	const reducerList = Array.isArray(reducersOrInitializer)
		? reducersOrInitializer
		: reducers;

	return (state = initialState, action) => {
		if (isInitAction(action)) {
			return initializer ? initializer(state) : state;
		}

		if (Array.isArray(reducerList)) {
			return reducerList.reduce(
				(state, reducer) => reducer(state, action),
				state,
			);
		}
		return state;
	};
};
